import axios from "axios";
import { UserEvent } from "../Types/userEvents";
import { getUserFromLS } from "../utils/getUserFromLS";

export type PublishUserEventResponse = {
  success: boolean;
  messageId: string;
  topic: string;
};

export type UserEventDraft = Omit<UserEvent, "clientId"> & {
  clientId?: string | null;
};

export class MissingUserEventClientIdError extends Error {
  constructor() {
    super("User event payload requires clientId");
    this.name = "MissingUserEventClientIdError";
  }
}

function normalizeClientId(clientId: string | null | undefined): string | null {
  const trimmed = clientId?.trim();
  return trimmed ? trimmed : null;
}

/** Forces user-scoped fields to the authenticated app context before publishing. */
export function withAuthenticatedUserContext(event: UserEventDraft): UserEvent {
  const user = getUserFromLS();
  const authenticatedUserId = user.id ?? null;
  const clientId = normalizeClientId(event.clientId) ?? normalizeClientId(user.client_id);

  if (!clientId) {
    throw new MissingUserEventClientIdError();
  }

  return { ...event, userId: authenticatedUserId, clientId } as UserEvent;
}

export const publishUserEvent = (
  event: UserEventDraft,
  callback?: (response: PublishUserEventResponse) => void,
  errorCallback?: (error: unknown) => void
) => {
  const topic = import.meta.env.VITE_USER_EVENT_TOPIC ?? null;
  let normalizedEvent: UserEvent;

  try {
    normalizedEvent = withAuthenticatedUserContext(event);
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    return Promise.resolve();
  }

  const request = axios("/api/user-events-publish", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      topic,
      event: normalizedEvent,
    },
  }) as unknown;

  return Promise.resolve(request)
    .then((response: any) => {
      if (callback) {
        callback((response?.data ?? { success: false, messageId: "", topic: topic ?? "" }) as PublishUserEventResponse);
      }
    })
    .catch((error) => {
      if (errorCallback) {
        errorCallback(error);
      }
    });
};
